import {Action, Module, Mutation} from 'vuex-module-decorators'
import store, {studyDefinitionModule} from "@/store";
import {MdbsCore} from "@/api/tsclient";
import BaseModule from "@/store/modules/BaseModule";
import VisitContextCache from "@/models/visit.context.cache";
import InstrumentContextCache from "@/models/instrument.context.cache";
import StudyPatientContextDto = MdbsCore.StudyPatientContextDto;

const studyPatientClient = new MdbsCore.StudyPatientClient();

@Module({namespaced: true, name: 'studyPatientContext', store: store})
export default class StudyPatientContextModule extends BaseModule {
    private readonly DEFINITIONTYPE_INTERMEDIATEVISIT: string = "IntermediateVisit";
    private readonly DEFINITIONTYPE_REQUIRED_FIELD: string = "RequiredField";

    _visitContexts: Array<VisitContextCache> = [];
    _standaloneInstruments: Array<InstrumentContextCache> = []

    get standaloneInstruments() {
        return this._standaloneInstruments;
    }

    _studyPatient: StudyPatientContextDto = new StudyPatientContextDto();

    get studyPatient() {
        return this._studyPatient;
    }

    get patientField() {
        return (identifier: string) => {
            const definitionId = studyDefinitionModule.studyDefinition.patient?.fields
                ?.find(f => f.identifier == identifier && !f.isDeleted)?.id;
            return this._studyPatient?.fields
                ?.find(f => f.definitionId == definitionId)?.values;
        }
    }

    get isPatientComplete() {
        const requiredDefinitionIds = studyDefinitionModule.studyDefinition.patient?.fields?.filter(f =>
            !f.isDeleted && f.rules?.some(r => !r.isDeleted && r.definitionType == this.DEFINITIONTYPE_REQUIRED_FIELD))
            .map(f => f.id) ?? [];

        let requiredFields = this._studyPatient?.fields?.filter(f=>
            f.definitionId && requiredDefinitionIds.includes(f.definitionId) && f.isVisible && !f.isDeleted) ?? [];

        return requiredFields.every(f => !!f.values?.value);
    }

    get visits() {
        return this._visitContexts;
    }

    get visibleVisits() {
        return this._visitContexts.filter(v => v.isVisible && !v.isIntermediateVisit)
    }

    get visibleIntermediateVisits() {
        return this._visitContexts.filter(v => v.isVisible && !v.isNew && v.isIntermediateVisit)
    }

    get visibleIntermediateVisitDefinitions() {
        return this._visitContexts.filter(v => v.isVisible &&  v.isNew && v.isIntermediateVisit)
    }

    get visitById() {
        return (visitId: string) => this._visitContexts?.find(v => v.id == visitId);
    }

    @Mutation
    setStudyPatient(studyPatient: StudyPatientContextDto) {
        this._studyPatient = studyPatient;

        const visitContexts = new Array<VisitContextCache>();
        for (let visitDefinition of studyDefinitionModule.studyDefinition.visits ?? []) {

            // get visits
            const visits = studyPatient.visits?.filter(v => v.definitionId == visitDefinition.id)  ?? [];

            // add an empty intermediate visit to create new visits
            if (visitDefinition.definitionType == this.DEFINITIONTYPE_INTERMEDIATEVISIT) {
                visitContexts.push(new VisitContextCache(visitDefinition, undefined, []))
            }

            // add an empty general visit to create a new visit, if no visit exists for this definition
            if (visitDefinition.definitionType != this.DEFINITIONTYPE_INTERMEDIATEVISIT && visits.length == 0){
                visitContexts.push(new VisitContextCache(visitDefinition, undefined, []))
            }

            // generate instruments
            for (let visit of visits) {
                const instrumentContexts = new Array<InstrumentContextCache>();
                if (visit != null) {
                    for (let visitInstrumentDefinition of visitDefinition?.instruments ?? []) {
                        const instrumentDefinition = studyDefinitionModule
                            .instrumentDefinitionById(visitInstrumentDefinition?.instrumentDefinitionId)
                        if (instrumentDefinition == null) continue; // TODO this is Error worthy

                        const instrument = visit.instruments
                            ?.find(i => i.definitionId == instrumentDefinition?.id)
                        instrumentContexts.push(new InstrumentContextCache(instrumentDefinition, instrument, visitInstrumentDefinition))
                    }
                }
                visitContexts.push(new VisitContextCache(visitDefinition, visit, instrumentContexts))
            }
        }
        this._visitContexts.updateValues(visitContexts);

        const standaloneInstruments = new Array<InstrumentContextCache>();
        for (let standaloneInstrumentDefinition of studyDefinitionModule.studyDefinition?.standaloneInstruments ?? []) {
            const instrumentDefinition = studyDefinitionModule
                .instrumentDefinitionById(standaloneInstrumentDefinition.instrumentDefinitionId)
            if (instrumentDefinition == null) continue;
            const instrument = studyPatient.standaloneInstruments
                ?.find(i => i.definitionId == instrumentDefinition?.id)
            standaloneInstruments.push(new InstrumentContextCache(instrumentDefinition, instrument, standaloneInstrumentDefinition))
        }
        this._standaloneInstruments.updateValues(standaloneInstruments);
    }

    @Mutation
    reset() {
        this._studyPatient = new StudyPatientContextDto();
        this._visitContexts = [];
    }

    @Action({rawError: true})
    async loadById(id: string) {
        await this.execute(async () => this.setStudyPatient(await studyPatientClient.getContextById(id))
        ).catch(() => this.reset());
    }

}
